import { ReactNode, useCallback, useEffect, useMemo, useState } from "react";
import { Box, Popover, Stack, styled } from "@mui/material";
import { FieldValues, FormProvider, useForm, useWatch, DefaultValues, Resolver } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import * as yup from "yup";
import { background, divider } from "@/theme/colors";
import { Button } from "@/components/elements/Button";
import { ConfirmDeleteModal } from "@/components/commons/ConfirmDeleteModal";

type Props<TForm extends FieldValues> = {
  anchorEl: HTMLElement | null;
  onClose: () => void;
  onApplyFilters?: (filters: TForm) => void;
  initialFilters?: TForm | null;
  defaultValues: TForm;
  schema: yup.AnyObjectSchema;
  isEmptyValue: (value: unknown) => boolean;
  children: ReactNode;
};

const buildDefaults = <TForm extends FieldValues>(defaults: TForm, overrides?: TForm | null): TForm => ({
  ...defaults,
  ...(overrides ?? {}),
});

export const FilterPopover = <TForm extends FieldValues>({
  anchorEl,
  onClose,
  onApplyFilters,
  initialFilters,
  defaultValues,
  schema,
  isEmptyValue,
  children,
}: Props<TForm>) => {
  const open = Boolean(anchorEl);

  const methods = useForm<TForm>({
    resolver: yupResolver(schema) as Resolver<TForm>,
    defaultValues: buildDefaults(defaultValues, initialFilters) as DefaultValues<TForm>,
    mode: "all",
    reValidateMode: "onBlur",
  });

  const filterValues = useWatch({ control: methods.control });
  const [isClearConfirmOpen, setIsClearConfirmOpen] = useState(false);

  useEffect(() => {
    if (!open) return;
    methods.reset(buildDefaults(defaultValues, initialFilters) as DefaultValues<TForm>);
  }, [open, initialFilters, methods, defaultValues]);

  const hasFilterData = useMemo(
    () => Object.values(filterValues ?? {}).some((v) => !isEmptyValue(v)),
    [filterValues, isEmptyValue],
  );

  const handleClearConfirm = useCallback(() => {
    methods.reset(defaultValues as DefaultValues<TForm>);
    setIsClearConfirmOpen(false);
  }, [methods, defaultValues]);

  const handleApply = useCallback(
    () =>
      methods.handleSubmit((values) => {
        onApplyFilters?.(values);
        onClose();
      })(),
    [methods, onApplyFilters, onClose],
  );

  return (
    <Popover
      open={open}
      anchorEl={anchorEl}
      onClose={onClose}
      anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
      transformOrigin={{ vertical: "top", horizontal: "left" }}
      sx={{
        "& .MuiPaper-root": {
          mt: 1,
          p: 0,
          borderRadius: "8px",
          boxShadow: "0px 8px 24px 0px #00000026",
        },
      }}
    >
      <PopoverContainer>
        <FormProvider {...methods}>
          <ContentContainer>
            <ScrollableContent>{children}</ScrollableContent>
          </ContentContainer>

          <FooterRow>
            <Stack flexDirection="row" gap={2}>
              <Button variant="ghost" onClick={onClose} size="sm">
                キャンセル
              </Button>
              <Button
                variant="text"
                onClick={() => hasFilterData && setIsClearConfirmOpen(true)}
                size="sm"
                disabled={!hasFilterData}
              >
                クリア
              </Button>
            </Stack>
            <Button onClick={handleApply} size="sm">
              条件を設定
            </Button>
          </FooterRow>
        </FormProvider>
      </PopoverContainer>

      <ConfirmDeleteModal
        open={isClearConfirmOpen}
        onClose={() => setIsClearConfirmOpen(false)}
        onOk={handleClearConfirm}
        message="設定した条件がすべてクリアされますがよろしいですか？"
        title=""
      />
    </Popover>
  );
};

const PopoverContainer = styled(Box)`
  width: 1056px;
  max-height: 80vh;
  display: flex;
  flex-direction: column;
  background: ${background.white};
  border-radius: 8px;
`;

const ContentContainer = styled(Box)`
  display: flex;
  flex-direction: column;
  flex: 1;
  min-height: 0;
`;

const ScrollableContent = styled(Box)(({ theme }) => ({
  flex: 1,
  overflowY: "auto",
  padding: theme.spacing(2, 3, 3, 3),
}));

const FooterRow = styled(Box)(({ theme }) => ({
  display: "flex",
  alignItems: "center",
  justifyContent: "space-between",
  padding: theme.spacing(2, 3),
  borderTop: `1px solid ${divider.middle}`,
}));
